import * as React from "react"
import { cn } from "@/lib/utils"
import { Icon } from "@/components/ui/icon"

export interface FilterButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  icon?: string // Material icon name
  label: string
  badge?: number | string
  active?: boolean
  variant?: "default" | "primary"
  iconPosition?: "left" | "right"
}

/**
 * FilterButton Component
 *
 * A standardized filter/tab button that replaces 12+ instances of duplicated button patterns.
 * Used for filtering content, tab navigation, and other selection interfaces.
 *
 * @example
 * ```tsx
 * <FilterButton
 *   icon="sports_soccer"
 *   label="Todos os Esportes"
 *   badge={12}
 *   active
 *   variant="primary"
 *   onClick={() => setFilter("all")}
 * />
 * ```
 */
const FilterButton = React.forwardRef<HTMLButtonElement, FilterButtonProps>(
  (
    {
      icon,
      label,
      badge,
      active = false,
      variant = "default",
      iconPosition = "left",
      className,
      ...props
    },
    ref
  ) => {
    const baseClasses = cn(
      // Layout
      "group flex h-10 shrink-0 items-center justify-center gap-x-2 rounded-xl px-4 md:px-6",
      // Transitions
      "transition-all duration-300 active:scale-95",
      // Active state
      active
        ? variant === "primary"
          ? "bg-primary text-primary-foreground shadow-lg shadow-primary/25 hover:shadow-xl hover:shadow-primary/25 border border-transparent font-bold"
          : "bg-primary/10 text-primary border border-primary/20 hover:bg-primary/15 font-semibold"
        : "bg-white dark:bg-white/5 border border-border text-muted-foreground dark:text-muted-foreground hover:bg-muted/30 dark:hover:bg-white/10 hover:border-border dark:hover:border-white/20 shadow-sm font-medium",
      className
    )

    return (
      <button ref={ref} className={baseClasses} {...props}>
        {/* Left Icon */}
        {icon && iconPosition === "left" && (
          <Icon name={icon} className={cn(!active && "text-muted-foreground dark:text-muted-foreground group-hover:text-muted-foreground dark:group-hover:text-muted-foreground")} />
        )}

        {/* Label */}
        <span className="text-sm leading-normal whitespace-nowrap">
          {label}
        </span>

        {/* Badge */}
        {badge !== undefined && (
          <span
            className={cn(
              "text-xs py-0.5 px-2 rounded-full font-bold",
              active
                ? "bg-white/20 text-current"
                : "bg-muted/30 dark:bg-white/10 text-muted-foreground dark:text-muted-foreground",
            )}
          >
            {badge}
          </span>
        )}

        {/* Right Icon */}
        {icon && iconPosition === "right" && (
          <Icon name={icon} className={cn(!active && "text-muted-foreground dark:text-muted-foreground group-hover:text-muted-foreground dark:group-hover:text-muted-foreground")} />
        )}
      </button>
    )
  }
)

FilterButton.displayName = "FilterButton"

export { FilterButton }
